'use client';

import { useState, type CSSProperties } from 'react';
import classNames from '@wilkins-software/classnames-tailwind-merge';
import { LuChevronDown as ChevronDown } from 'react-icons/lu';
import { useNavigation } from './NavigationContext';
import type { NavMenuDropdownEntry } from './NAV_ITEMS';

interface NavDropdownItemProps {
  text: string;
  dropdown: NavMenuDropdownEntry[];
  idx: number;
}

export function NavDropdownItem({ text, dropdown, idx }: NavDropdownItemProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { setIsMenuOpen } = useNavigation();

  return (
    <li
      className="nav-item mobile-nav-item group/dropdown relative"
      style={{ '--mobile-nav-stagger': String(idx) } as CSSProperties}
    >
      <button
        type="button"
        className={classNames(
          'nav-link flex w-full items-center justify-between gap-1 transition-colors',
          'max-lg:rounded-[28px] max-lg:border max-lg:border-black/5 max-lg:bg-white/70 max-lg:px-6 max-lg:py-5 max-lg:text-3xl max-lg:font-bold max-lg:leading-none max-lg:tracking-tighter max-lg:text-slate-900 max-lg:shadow-[0_8px_32px_rgba(0,0,0,0.04)] max-lg:backdrop-blur-xl',
          'max-lg:hover:border-primary/20 max-lg:hover:bg-white max-lg:hover:text-primary',
          'lg:w-auto lg:justify-start lg:border-0 lg:bg-transparent lg:p-3 lg:text-xs lg:font-medium lg:uppercase lg:tracking-wide lg:shadow-none lg:backdrop-blur-none',
        )}
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => {
          if (window.matchMedia('(min-width: 1024px)').matches) {
            setIsOpen(true);
          }
        }}
        onMouseLeave={() => {
          if (window.matchMedia('(min-width: 1024px)').matches) {
            setIsOpen(false);
          }
        }}
        aria-expanded={isOpen}
      >
        {text}
        <ChevronDown
          className={classNames(
            'h-5 w-5 transition-transform duration-200 lg:h-3 lg:w-3',
            { 'rotate-180': isOpen },
          )}
        />
      </button>

      <div
        className={classNames(
          'z-50 transition-all duration-200',
          // Mobile: inline accordion under the trigger
          'max-lg:relative max-lg:top-auto max-lg:left-auto max-lg:mt-2 max-lg:overflow-hidden max-lg:rounded-[24px] max-lg:border max-lg:border-black/5 max-lg:bg-white/80 max-lg:shadow-[0_8px_32px_rgba(0,0,0,0.04)] max-lg:backdrop-blur-xl',
          // Desktop: floating dropdown
          'lg:absolute lg:top-full lg:left-0 lg:mt-0 lg:min-w-[220px] lg:rounded-md lg:border-gray-200 lg:bg-white lg:shadow-lg lg:backdrop-blur-none',
          // CSS-only reveal so the dropdown links stay reachable before
          // hydration and when JS never runs; the JS state machine below
          // drives the same classes once it is live.
          'lg:group-hover/dropdown:visible lg:group-hover/dropdown:translate-y-0 lg:group-hover/dropdown:opacity-100',
          'lg:group-focus-within/dropdown:visible lg:group-focus-within/dropdown:translate-y-0 lg:group-focus-within/dropdown:opacity-100',
          {
            'max-lg:max-h-[28rem] max-lg:opacity-100 lg:visible lg:translate-y-0 lg:opacity-100':
              isOpen,
            'max-lg:pointer-events-none max-lg:max-h-0 max-lg:border-0 max-lg:opacity-0 lg:invisible lg:-translate-y-2 lg:opacity-0':
              !isOpen,
          },
        )}
        onMouseEnter={() => {
          if (window.matchMedia('(min-width: 1024px)').matches) {
            setIsOpen(true);
          }
        }}
        onMouseLeave={() => {
          if (window.matchMedia('(min-width: 1024px)').matches) {
            setIsOpen(false);
          }
        }}
      >
        {dropdown.map((entry, i) => {
          if (entry.kind === 'group') {
            return (
              <div
                key={`dropdown-group-${idx}-${i}`}
                className="border-b border-black/5 py-1 last:border-b-0 lg:border-gray-100"
              >
                <div className="px-5 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500 lg:px-4 lg:pt-2">
                  {entry.groupLabel}
                </div>
                {entry.items.map((item, j) => {
                  const isExternalHref = item.href.startsWith('http');
                  return (
                    <a
                      key={`dropdown-group-${idx}-${i}-link-${j}`}
                      href={item.href}
                      className="block px-5 py-3 text-lg font-medium tracking-tight text-slate-800 transition-colors hover:bg-primary/5 hover:text-primary lg:px-4 lg:py-2 lg:text-sm lg:font-normal lg:text-gray-700 lg:hover:bg-gray-50"
                      onClick={() => setIsMenuOpen(false)}
                      {...(isExternalHref
                        ? {
                            target: '_blank',
                            rel: 'noopener noreferrer',
                          }
                        : {})}
                    >
                      {item.text}
                    </a>
                  );
                })}
              </div>
            );
          }
          return (
            <a
              key={`dropdown-link-${idx}-${i}`}
              href={entry.href}
              className="block px-5 py-3 text-lg font-medium tracking-tight text-slate-800 transition-colors hover:bg-primary/5 hover:text-primary first:rounded-t-[24px] last:rounded-b-[24px] lg:px-4 lg:py-3 lg:text-sm lg:font-normal lg:text-gray-700 lg:hover:bg-gray-50 lg:first:rounded-t-md lg:last:rounded-b-md"
              onClick={() => setIsMenuOpen(false)}
            >
              {entry.text}
            </a>
          );
        })}
      </div>
    </li>
  );
}
